import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { normalizeWord, normalizeClue } from '@/lib/textUtils';

export interface WordSuggestion {
  id: number;
  word: string;
  clue: string | null;
  status: 'pending' | 'approved' | 'rejected';
  admin_note: string | null;
  created_at: string;
  reviewed_at: string | null;
  submitted_by?: string;
}

export interface WordExistsResult {
  exists: boolean;
  pending: boolean;
  inMainDatabase: boolean;
  inApproved: boolean;
}

export function useWordSuggestions() {
  const [loading, setLoading] = useState(false);
  const [mySuggestions, setMySuggestions] = useState<WordSuggestion[]>([]);
  const [pendingSuggestions, setPendingSuggestions] = useState<WordSuggestion[]>([]);

  const submitWord = async (word: string, clue: string) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Du måste vara inloggad');

      const { error } = await supabase
        .from('word_suggestions')
        .insert({
          user_id: user.id,
          word: word.toUpperCase(),
          clue,
          status: 'pending',
        });

      if (error) throw error;
      return { error: null, message: 'Förslag skickat!' };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte skicka förslag';
      return { error: message, message: null };
    } finally {
      setLoading(false);
    }
  };

  const checkWordExists = useCallback(async (word: string): Promise<WordExistsResult> => {
    try {
      const upperWord = word.toUpperCase();

      // Check in main words database
      const { data: mainWord } = await supabase
        .from('words')
        .select('id')
        .eq('word_upper', upperWord)
        .maybeSingle();

      // Check in pending suggestions
      const { data: pendingSuggestion } = await supabase
        .from('word_suggestions')
        .select('id, status')
        .eq('word', upperWord)
        .in('status', ['pending', 'approved'])
        .maybeSingle();

      return {
        exists: !!mainWord || !!pendingSuggestion,
        pending: pendingSuggestion?.status === 'pending',
        inMainDatabase: !!mainWord,
        inApproved: pendingSuggestion?.status === 'approved',
      };
    } catch {
      return { exists: false, pending: false, inMainDatabase: false, inApproved: false };
    }
  }, []);

  const fetchMySuggestions = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Ej inloggad');

      const { data, error } = await supabase
        .from('word_suggestions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMySuggestions((data || []) as WordSuggestion[]);
      return { error: null };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte hämta förslag';
      return { error: message };
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingSuggestions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('word_suggestions')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fetch display names for all user_ids
      const userIds = [...new Set((data || []).map(s => s.user_id).filter(Boolean))];
      let profileMap: Record<string, string> = {};
      
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name')
          .in('id', userIds);
        
        profileMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = p.display_name;
          return acc;
        }, {} as Record<string, string>);
      }
      
      const suggestions = (data || []).map(s => ({
        ...s,
        submitted_by: s.user_id ? profileMap[s.user_id] || 'Okänd' : 'Okänd',
      }));
      
      setPendingSuggestions(suggestions as WordSuggestion[]);
      return { error: null };
    } catch (error) {
      console.error('Error fetching pending suggestions:', error);
      const message = error instanceof Error ? error.message : 'Kunde inte hämta förslag';
      return { error: message };
    } finally {
      setLoading(false);
    }
  };

  const reviewSuggestion = async (
    suggestionId: number, 
    reviewAction: 'approve' | 'reject', 
    editedClue?: string
  ) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Ej inloggad');

      const newStatus = reviewAction === 'approve' ? 'approved' : 'rejected';

      // Update clue if edited
      if (editedClue !== undefined) {
        await supabase
          .from('word_suggestions')
          .update({ clue: editedClue })
          .eq('id', suggestionId);
      }

      const { error } = await supabase
        .from('word_suggestions')
        .update({
          status: newStatus,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', suggestionId);

      if (error) throw error;

      // If approved, add to main words database in BOTH directions
      if (reviewAction === 'approve') {
        const { data: suggestion } = await supabase
          .from('word_suggestions')
          .select('word, clue')
          .eq('id', suggestionId)
          .single();

        if (suggestion && suggestion.clue) {
          // Direction 1: Word -> Clue (e.g., HUND -> "Djur")
          let { data: existingWord1 } = await supabase
            .from('words')
            .select('id')
            .eq('word_upper', suggestion.word.toUpperCase())
            .maybeSingle();

          if (!existingWord1) {
            const { data: newWord, error: wordError } = await supabase
              .from('words')
              .insert({
                word: normalizeWord(suggestion.word),
                word_upper: suggestion.word.toUpperCase(),
                length: suggestion.word.length,
              })
              .select('id')
              .single();
            if (!wordError) existingWord1 = newWord;
          }

          if (existingWord1) {
            await supabase
              .from('clues')
              .insert({
                word_id: existingWord1.id,
                clue: normalizeClue(suggestion.clue),
                clue_upper: suggestion.clue.toUpperCase(),
              });
          }

          // Direction 2: Add each word in the clue as a separate word entry
          const wordAsClue = suggestion.word.trim();
          const clueWords = suggestion.clue.trim().split(/\s+/).filter(w => w.length >= 2);

          for (const clueWord of clueWords) {
            const clueWordUpper = clueWord.toUpperCase().replace(/[^A-ZÅÄÖ]/g, '');
            if (!clueWordUpper || clueWordUpper.length < 2) continue;

            let { data: existingWord2 } = await supabase
              .from('words')
              .select('id')
              .eq('word_upper', clueWordUpper)
              .maybeSingle();

            if (!existingWord2) {
              const { data: newWord2, error: wordError2 } = await supabase
                .from('words')
                .insert({
                  word: normalizeWord(clueWord.replace(/[^a-zA-ZåäöÅÄÖ]/g, '')),
                  word_upper: clueWordUpper,
                  length: clueWordUpper.length,
                })
                .select('id')
                .single();
              if (!wordError2) existingWord2 = newWord2;
            }

            if (existingWord2) {
              // Check if this clue already exists for this word
              const { data: existingClue } = await supabase
                .from('clues')
                .select('id')
                .eq('word_id', existingWord2.id)
                .eq('clue_upper', wordAsClue.toUpperCase())
                .maybeSingle();

              if (!existingClue) {
                await supabase
                  .from('clues')
                  .insert({
                    word_id: existingWord2.id,
                    clue: normalizeClue(wordAsClue),
                    clue_upper: wordAsClue.toUpperCase(),
                  });
              }
            }
          }
        }
      }

      // Remove from pending list
      setPendingSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      return { error: null };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte granska förslag';
      return { error: message };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    mySuggestions,
    pendingSuggestions,
    submitWord,
    checkWordExists,
    fetchMySuggestions,
    fetchPendingSuggestions,
    reviewSuggestion,
  };
}
